/**
 * Capacity Overview Dialog
 * Standalone MODAL dialog showing service breakdown and children list for a group on a
 * specific date.
 *
 * The capacity page no longer opens this — a grid cell now selects the cell and the same
 * roster renders inside the non-modal detail Sheet (C3). This dialog remains the modal
 * host for the Shift Coverage Dashboard (`shifts/CoverageCard.tsx`), whose call site is
 * unchanged: the public props below are identical, and the body is the same
 * `CapacityCellRoster` the Sheet mounts.
 */

import { useMemo } from 'react';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/macro';
import { formatDateLong } from '@/lib/format';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CapacityCellRoster } from './CapacityCellRoster';
import type { CalendarChildView } from '@/types/calendar';

interface CapacityOverviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: string;
  groupName?: string;
  date: string;
  onChildClick?: (childId: string) => void;
  /** Pre-fetched children data. When provided, skips the API call. */
  children?: CalendarChildView[];
  /** Pre-computed flag for service breakdown availability. */
  hasServiceBreakdownHint?: boolean;
  /** Pre-computed peak service code. */
  peakServiceHint?: string;
}

export function CapacityOverviewDialog({
  open,
  onOpenChange,
  groupId,
  groupName,
  date,
  onChildClick,
  children: childrenProp,
  hasServiceBreakdownHint,
  peakServiceHint,
}: CapacityOverviewDialogProps) {
  const { i18n } = useLingui();

  const formattedDate = useMemo(() => formatDateLong(date, i18n.locale), [date, i18n.locale]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>
            {groupName ? (
              <Trans>Capacity Overview for {formattedDate} — {groupName}</Trans>
            ) : (
              <Trans>Capacity Overview for {formattedDate}</Trans>
            )}
          </DialogTitle>
        </DialogHeader>
        <CapacityCellRoster
          groupId={groupId}
          groupName={groupName}
          date={date}
          children={childrenProp}
          hasServiceBreakdownHint={hasServiceBreakdownHint}
          peakServiceHint={peakServiceHint}
          // Selecting a child closes the modal first, exactly as before.
          onChildClick={
            onChildClick
              ? (childId) => {
                  onOpenChange(false);
                  onChildClick(childId);
                }
              : undefined
          }
        />
      </DialogContent>
    </Dialog>
  );
}
